import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguageData } from '../contexts/LanguageContext';
import './PageHeader.css';
import './BrainWarmup.css';

const PageHeader = ({ 
  breadcrumbs = [], 
  language, 
  proficiencyLevel, 
  onLanguageChange, 
  onLevelChange,
  rightContent,
  disableDropdowns = false,
  disableLevelDropdown = false,
  // showProgress = false,
  // progressPercent = 0,
  // progressText = ''
}) => {
  const navigate = useNavigate();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [showSwitchPopup, setShowSwitchPopup] = useState(false);
  const [pendingLang, setPendingLang] = useState(null);
  const [userLevels, setUserLevels] = useState({});
  const [showWarmupPopup, setShowWarmupPopup] = useState(false);
  const [warmupConfirmed, setWarmupConfirmed] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
  const langDropdownRef = useRef(null);
  const levelDropdownRef = useRef(null);

  // Fetch user assessed levels
  useEffect(() => {
    axios.get((process.env.REACT_APP_API_URL || '/lb-api') + '/pre-assessment/user-levels', { withCredentials: true })
      .then(res => { if (res.data.success && res.data.levels) { const m = {}; res.data.levels.forEach(l => { m[l.language_code] = l.assigned_level; }); setUserLevels(m); } })
      .catch(() => {});
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setShowLanguageDropdown(false);
      }
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(e.target)) {
        setShowLevelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Use shared language data from context
  const { languages: languagesData, proficiencyLevels: levelsData, fetchLevelsForLanguage } = useLanguageData();

  // Fetch levels when language changes
  React.useEffect(() => {
    if (language) {
      fetchLevelsForLanguage(language);
    }
  }, [language, fetchLevelsForLanguage]);

  // Auto-select first active level when levels change (e.g., after language switch)
  const prevLanguageRef = React.useRef(language);
  React.useEffect(() => {
    if (!levelsData || Object.keys(levelsData).length === 0) return;
    
    const languageChanged = prevLanguageRef.current !== language;
    prevLanguageRef.current = language;
    
    // Check if current proficiencyLevel exists in the new levels
    const currentExists = proficiencyLevel && levelsData[proficiencyLevel];
    
    if ((!currentExists || languageChanged) && onLevelChange) {
      // Find the first active level
      const firstActiveKey = Object.entries(levelsData).find(
        ([, data]) => (typeof data === 'object' ? data.active !== false : true)
      );
      if (firstActiveKey) {
        onLevelChange(firstActiveKey[0]);
      }
    }
  }, [levelsData, proficiencyLevel, onLevelChange, language]);

  // Transform data for display
  const languages = Object.entries(languagesData).map(([code, data]) => ({
    value: code,
    label: data.label,
    flag: data.flag || `/images/flag-${code}.svg`,
    active: data.active !== false,
  }));

  const proficiencyLevels = Object.entries(levelsData).map(([key, data]) => ({
    value: key,
    label: typeof data === 'string' ? data : (typeof data?.label === 'string' ? data.label : key),
    active: typeof data === 'object' ? Boolean(data.active) : true,
  }));

  const achievedLevel = userLevels[language];
  const achievedMap = language === 'ja'
    ? {'N5':'beginner-n5','N4':'pre-intermediate-n4','N3':'intermediate-n3','N2':'advanced-n2','N1':'proficient-n1'}
    : {'A1':'beginner-a1','A2':'elementary-a2','B1':'pre-intermediate-b1','B2':'intermediate-b2','C1':'advanced-c1','C2':'proficient-c2'};
  const achievedKey = achievedMap[achievedLevel];
  const achievedIdx = achievedKey ? proficiencyLevels.findIndex(function(l) { return l.value === achievedKey; }) : -1;
  const restrictedLevels = achievedIdx >= 0 ? proficiencyLevels.map(function(l, i) { return Object.assign({}, l, {active: l.active && i <= achievedIdx}); }) : proficiencyLevels;
  const selectedLanguage = languages.find(lang => lang.value === language) || languages[0] || { label: 'German', flag: '/images/flag-german.svg', active: true };
  const selectedLevel = restrictedLevels.find(level => level.value === proficiencyLevel) || restrictedLevels[0] || { label: 'Beginner A1', active: true };
  
  // Ensure selectedLevel.label is always a string
  const selectedLevelLabel = typeof selectedLevel?.label === 'string' ? selectedLevel.label : 'Beginner A1';

  const handleLanguageChange = (lang) => {
    if (!lang.active) return;
    setShowLanguageDropdown(false);
    if (lang.value === language) return; // Same language, do nothing
    if (Object.keys(userLevels).length > 0) { setPendingLang(lang); setShowSwitchPopup(true); return; }
  };



  const handleLevelChange = (level) => {
    if (level.active) {
      setShowLevelDropdown(false);
      if (onLevelChange) {
        onLevelChange(level.value);
      }
    }
  };

  return (
    <div className="page-header-bar">
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="breadcrumb-separator">/</span>}
            <span 
              className={`breadcrumb-item ${crumb.current ? 'current' : ''}`}
              onClick={() => {
                if (crumb.current) return;
                if (crumb.onClick) { crumb.onClick(); return; }
                if (crumb.path) navigate(crumb.path, { state: { language, proficiencyLevel } });
              }}
            >
              {crumb.label}
            </span>
          </React.Fragment>
        ))}
      </div>
      
      {rightContent ? (
        rightContent
      ) : (
        <div className="header-right-section">
          <div className="language-badges">
            <div className="badge-dropdown" ref={langDropdownRef} style={disableDropdowns ? {opacity: 0.3, pointerEvents: 'none'} : {}} onClick={() => {
              setShowLanguageDropdown(!showLanguageDropdown);
              setShowLevelDropdown(false);
            }}>
              <img src={selectedLanguage.flag} alt={`${selectedLanguage.label} flag`} className="flag-icon" />
              <span>{selectedLanguage.label}</span>
              <img 
                src="/images/dropdownarrow.svg" 
                alt="Dropdown" 
                className={`dropdown-arrow-icon ${showLanguageDropdown ? 'open' : ''}`}
              />
              {showLanguageDropdown && (
                <div className="dropdown-menu">
                  {[...languages].sort((a,b)=>a.active===b.active?0:a.active?-1:1).map((lang) => (
                    <div 
                      key={lang.value} 
                      className={`dropdown-item ${language === lang.value ? 'active' : ''} ${!lang.active ? 'inactive' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLanguageChange(lang);
                      }}
                    >
                      <img src={lang.flag} alt={`${lang.label} flag`} className="dropdown-flag" />
                      <span>{lang.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="badge-dropdown" ref={levelDropdownRef} style={(disableDropdowns || disableLevelDropdown) ? {opacity: 0.3, pointerEvents: 'none'} : {}} onClick={() => {
              setShowLevelDropdown(!showLevelDropdown);
              setShowLanguageDropdown(false);
            }}>
              <span>{selectedLevelLabel}</span>
              <img 
                src="/images/dropdownarrow.svg" 
                alt="Dropdown" 
                className={`dropdown-arrow-icon ${showLevelDropdown ? 'open' : ''}`}
              />
              {showLevelDropdown && (
                <div className="dropdown-menu">
                  {restrictedLevels.map((level) => (
                    <div 
                      key={level.value} 
                      className={`dropdown-item ${proficiencyLevel === level.value ? 'active' : ''} ${!level.active ? 'inactive' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLevelChange(level);
                      }}
                    >
                      <span>{level.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* {showProgress && (
            <div className="header-progress-section">
              <div className="header-progress-bar">
                <div className="header-progress-fill" style={{ width: `${progressPercent}%` }}></div>
              </div>
              <div className="header-progress-info">
                <span className="header-progress-percent">{progressPercent}%</span>
                <span className="header-progress-text">{progressText}</span>
              </div>
            </div>
          )} */}
        </div>
      )}
      {showSwitchPopup&&(<div className="bw-confirm-overlay"><div className="bw-confirm-modal bw-submit-modal" onClick={e=>e.stopPropagation()}><h3 className="bw-submit-title"><img src="/images/Language.svg" alt="" width="20" height="20" style={{marginRight:"8px",verticalAlign:"middle"}} />Switch Language?</h3><p className="bw-submit-text">You&#8217;re switching from <strong style={{color:"#7500C0"}}>{languages.find(l=>l.value===language)?.label||language} ({userLevels[language]||proficiencyLevel})</strong> to <strong style={{color:"#7500C0"}}>{pendingLang?.label||""}{userLevels[pendingLang?.value]?" ("+userLevels[pendingLang?.value]+")":""}</strong>. Your {languages.find(l=>l.value===language)?.label||language} progress is safe - you can continue learning from where you left off anytime.</p><p className="bw-submit-warning"><strong>{userLevels[pendingLang?.value]?"You will continue your "+(pendingLang?.label||"")+" learning journey from your last saved progress.":"Starting "+(pendingLang?.label||"")+" will begin with a Brain Warm-up to understand your current proficiency and personalize your learning journey."}</strong></p><div className="bw-confirm-actions"><button className="bw-confirm-cancel" onClick={()=>{setShowSwitchPopup(false);if(userLevels[pendingLang?.value]){if(onLanguageChange)onLanguageChange(pendingLang.value);navigate("/explore-learning",{state:{language:pendingLang.value,proficiencyLevel:userLevels[pendingLang.value]}});setPendingLang(null);}else{setShowWarmupPopup(true);}}}>Switch to {pendingLang?.label||""}</button><button className="bw-confirm-yes" onClick={()=>{setShowSwitchPopup(false);setPendingLang(null);}}>Stay in {languages.find(l=>l.value===language)?.label||language}</button></div></div></div>)}
      {showWarmupPopup&&(<div className="home-new-popup-overlay"><div className="home-new-popup"><div className="home-new-popup-header"><span>Please read the following before beginning your Brain Warm-up</span><button className="home-new-popup-close" onClick={()=>{setShowWarmupPopup(false);setWarmupConfirmed(false);}}>&times;</button></div><div className="home-new-popup-items"><div className="home-new-popup-item"><img src="/images/Star-black.svg" alt="" width="20" height="20" /><div><h4>Complete this before you start learning:</h4><p>A quick language warm-up to build an honest picture of your level. It&#8217;s fine if you&#8217;re unsure&#8212;give it your best answer.</p></div></div><div className="home-new-popup-item"><img src="/images/Chart-black.svg" alt="" width="20" height="20" /><div><h4>Questions adapt as you go:</h4><p>The difficulty adjusts based on your answers&#8212;some questions may feel easier or harder than expected.</p></div></div><div className="home-new-popup-item"><img src="/images/black-info.svg" alt="" width="20" height="20" /><div><h4>Don&#8217;t exit before submitting:</h4><p>Closing or navigating away before you finish will reset the assessment. You&#8217;ll need to start over.</p></div></div><div className="home-new-popup-item"><img src="/images/time-black.svg" alt="" width="20" height="20" /><div><h4>Session Timeout?</h4><p>Your answers are safe if your session expires, all completed responses are automatically submitted - Nothing is lost.</p></div></div></div><div className="home-new-popup-divider"></div><label className="home-new-popup-checkbox"><input type="checkbox" checked={warmupConfirmed} onChange={e=>setWarmupConfirmed(e.target.checked)} /><span>I understand my language selection cannot be changed once the challenge begins</span></label><div className="home-new-popup-footer"><button className="home-new-popup-start" onClick={()=>{setShowWarmupPopup(false);setWarmupConfirmed(false);navigate('/brain-warmup',{state:{language:pendingLang?.value||language}});}} disabled={!warmupConfirmed}>Start Brain Warm-up</button><span className="home-new-popup-hint">Take your time and trust your instincts.</span></div></div></div>)}
    </div>
  );
};

export default PageHeader;




